import React, { Component } from 'react';
import { ClockIcon, BookOpenIcon, SparklesIcon, ClipboardCheckIcon, XIcon, CheckIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
type Row = {
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  category: string;
  before: string;
  after: string;
  metric: string;
};
const rows: Row[] = [{
  icon: ClockIcon,
  category: 'Pianificazione',
  before: 'Passi ore a cercare idee su Pinterest, YouTube o vecchi libri.',
  after: 'Percorsi artistici già pronti e organizzati.',
  metric: '−90% tempo'
}, {
  icon: BookOpenIcon,
  category: 'Lezioni',
  before: "Improvvisi attività all'ultimo minuto senza una vera progressione annuale.",
  after: 'Zero stress Apri il volume e insegni subito.',
  metric: 'Zero stress'
}, {
  icon: SparklesIcon,
  category: 'Coinvolgimento',
  before: 'Molti bambini si annoiano o perdono interesse dopo pochi minuti.',
  after: 'Laboratori artistici che fanno brillare gli occhi ai bambini.',
  metric: '+100% attenzione'
}, {
  icon: ClipboardCheckIcon,
  category: 'Valutazione',
  before: 'Non sai come documentare il percorso artistico degli alunni.',
  after: 'Rubriche e materiali già organizzati.',
  metric: 'Portfolio pronto'
}];
export function ContrastSection() {
  return <section className="w-full bg-bg-main py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-137">
      {/* Subtle accent glows */}
      <div className="absolute top-0 -left-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(239,68,68,0.08) 0%, transparent 60%)'
    }} aria-hidden="true" data-id="element-138" />
      <div className="absolute bottom-0 -right-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 60%)'
    }} aria-hidden="true" data-id="element-139" />

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-140">
        {/* Section eyebrow + headline */}
        <div className="text-center mb-10 sm:mb-14" data-id="element-141">
          <span className="inline-flex items-center gap-2 text-primary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full mb-4" data-id="element-142">
            ⚡ La trasformazione
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.1] mb-4" data-id="element-143">
            Da insegnante stressata
            <br className="hidden sm:block" data-id="element-144" />
            <span className="text-primary" data-id="element-145">
              {' '}
              a docente creativa, organizzata e serena.
            </span>
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-146">
            Ecco cosa cambia{' '}
            <span className="text-text-main font-bold" data-id="element-147">
              dalla prima settimana
            </span>{' '}
            con ARTE PRONTA nelle tue mani.
          </p>
        </div>

        {/* Time cost callout — high-impact stat */}
        <div className="relative bg-bg-alt border border-badge-red/30 rounded-2xl sm:rounded-3xl p-6 sm:p-10 mb-10 sm:mb-14 overflow-hidden" data-id="element-148">
          <div className="absolute inset-0 pointer-events-none opacity-60" style={{
          background: 'radial-gradient(ellipse at top, rgba(239,68,68,0.10) 0%, transparent 70%)'
        }} aria-hidden="true" data-id="element-149" />
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-center" data-id="element-150">
            <div className="flex md:flex-col items-center md:items-center justify-center gap-3 md:gap-1 md:border-r md:border-badge-red/20 md:pr-8" data-id="element-151">
              <div className="flex items-baseline gap-1" data-id="element-152">
                <span className="text-5xl sm:text-7xl font-black text-badge-red leading-none tracking-tighter" data-id="element-153">
                  150
                </span>
                <span className="text-2xl sm:text-3xl font-black text-badge-red leading-none" data-id="element-154">
                  +
                </span>
              </div>
              <span className="text-text-sec text-xs sm:text-sm font-bold uppercase tracking-wider md:mt-1" data-id="element-155">
                ore / anno
              </span>
            </div>
            <div className="text-center md:text-left" data-id="element-156">
              <div className="inline-flex items-center gap-1.5 bg-badge-red/15 text-badge-red font-black text-[10px] sm:text-xs uppercase tracking-wider px-3 py-1 rounded-full mb-3" data-id="element-157">
                <TrendingDownIcon className="w-3.5 h-3.5" data-id="element-158" />
                Il vero costo
              </div>
              <p className="text-text-main text-base sm:text-xl font-semibold leading-relaxed" data-id="element-159">
                Pianificare manualmente ti toglie da{' '}
                <span className="text-badge-red font-black" data-id="element-160">
                  3 a 5 ore a settimana
                </span>
                . Sono{' '}
                <span className="text-badge-red font-black" data-id="element-161">
                  l'equivalente di un mese intero
                </span>{' '}
                di lavoro all'anno bruciato in calcoli, schede e correzioni.
              </p>
            </div>
          </div>
        </div>

        {/* Comparison table */}
        <div className="bg-bg-alt rounded-2xl sm:rounded-3xl shadow-2xl border border-border-light overflow-hidden" data-id="element-162">
          {/* Header */}
          <div className="grid grid-cols-2 border-b border-border-light" data-id="element-163">
            <div className="px-4 sm:px-8 py-4 sm:py-6 bg-badge-red/5 border-r border-border-light" data-id="element-164">
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-165">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-badge-red/15 border border-badge-red/30 flex items-center justify-center flex-shrink-0" data-id="element-166">
                  <XIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-badge-red" strokeWidth={3} data-id="element-167" />
                </div>
                <div className="min-w-0" data-id="element-168">
                  <p className="text-[9px] sm:text-[11px] text-text-sec font-bold uppercase tracking-[0.15em] leading-none mb-1" data-id="element-169">
                    Oggi, senza
                  </p>
                  <p className="text-red-400 font-black text-xs sm:text-base leading-tight truncate" data-id="element-170">
                    La routine attuale
                  </p>
                </div>
              </div>
            </div>
            <div className="px-4 sm:px-8 py-4 sm:py-6 bg-cta-green/5" data-id="element-171">
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-172">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-cta-green/15 border border-cta-green/30 flex items-center justify-center flex-shrink-0" data-id="element-173">
                  <CheckIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cta-green" strokeWidth={3} data-id="element-174" />
                </div>
                <div className="min-w-0" data-id="element-175">
                  <p className="text-[9px] sm:text-[11px] text-text-sec font-bold uppercase tracking-[0.15em] leading-none mb-1" data-id="element-176">
                    Da domani, con
                  </p>
                  <p className="text-cta-green font-black text-xs sm:text-base leading-tight truncate" data-id="element-177">
                    ARTE PRONTA
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-border-light" data-id="element-178">
            {rows.map((row, i) => {
            const Icon = row.icon;
            return <div key={i} className="grid grid-cols-2 group" data-id="element-179">
                  {/* BEFORE cell */}
                  <div className="px-4 sm:px-8 py-5 sm:py-7 flex items-start gap-3 sm:gap-4 border-r border-border-light bg-black/20 transition-colors group-hover:bg-black/30" data-id="element-180">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-badge-red/10 border border-badge-red/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-181">
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-badge-red/70" strokeWidth={2} data-id="element-182" />
                    </div>
                    <div className="min-w-0" data-id="element-183">
                      <p className="text-[9px] sm:text-[10px] text-text-sec font-bold uppercase tracking-[0.15em] mb-1" data-id="element-184">
                        {row.category}
                      </p>
                      <p className="text-text-sec font-medium text-sm sm:text-base leading-snug line-through decoration-badge-red/40 decoration-1" data-id="element-185">
                        {row.before}
                      </p>
                    </div>
                  </div>

                  {/* AFTER cell */}
                  <div className="px-4 sm:px-8 py-5 sm:py-7 flex items-start gap-3 sm:gap-4 bg-cta-green/[0.04] transition-colors group-hover:bg-cta-green/[0.08]" data-id="element-186">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-cta-green/15 border border-cta-green/30 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-[0_0_12px_rgba(16,185,129,0.15)]" data-id="element-187">
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-cta-green" strokeWidth={2.5} data-id="element-188" />
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-189">
                      <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-190">
                        <p className="text-[9px] sm:text-[10px] text-cta-green font-black uppercase tracking-[0.15em]" data-id="element-191">
                          {row.category}
                        </p>
                        <span className="inline-flex items-center gap-1 bg-cta-green/15 text-cta-green text-[9px] sm:text-[10px] font-black px-2 py-0.5 rounded-full border border-cta-green/20" data-id="element-192">
                          <TrendingUpIcon className="w-2.5 h-2.5" strokeWidth={3} data-id="element-193" />
                          {row.metric}
                        </span>
                      </div>
                      <p className="text-text-main font-bold text-sm sm:text-base leading-snug" data-id="element-194">
                        {row.after}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {/* Bottom summary bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border-light border-t border-border-light" data-id="element-195">
            <div className="bg-bg-alt px-4 py-4 sm:py-5 text-center" data-id="element-196">
              <p className="text-cta-green font-black text-2xl sm:text-3xl tracking-tight leading-none" data-id="element-197">
                +150h
              </p>
              <p className="text-text-sec text-[10px] sm:text-xs font-bold uppercase tracking-wider mt-1.5" data-id="element-198">
                Risparmiate ogni anno
              </p>
            </div>
            <div className="bg-bg-alt px-4 py-4 sm:py-5 text-center" data-id="element-199">
              <p className="text-cta-green font-black text-2xl sm:text-3xl tracking-tight leading-none" data-id="element-200">
                2 clic
              </p>
              <p className="text-text-sec text-[10px] sm:text-xs font-bold uppercase tracking-wider mt-1.5" data-id="element-201">
                Per ogni lezione pronta
              </p>
            </div>
            <div className="bg-bg-alt px-4 py-4 sm:py-5 text-center" data-id="element-202">
              <p className="text-cta-green font-black text-2xl sm:text-3xl tracking-tight leading-none" data-id="element-203">
                100%
              </p>
              <p className="text-text-sec text-[10px] sm:text-xs font-bold uppercase tracking-wider mt-1.5" data-id="element-204">
                Allineato al curriculum
              </p>
            </div>
          </div>
        </div>

        {/* Closing line */}
        <p className="text-center text-text-sec text-sm sm:text-base mt-8 sm:mt-10 max-w-2xl mx-auto leading-relaxed" data-id="element-205">
          La differenza non è nel{' '}
          <span className="text-text-main font-bold" data-id="element-206">cosa</span> insegni.
          <br className="hidden sm:block" data-id="element-207" />È nel{' '}
          <span className="text-primary font-black" data-id="element-208">tempo</span> e nell'
          <span className="text-primary font-black" data-id="element-209">energia</span> che ti
          restano per la tua vita.
        </p>
      </div>
    </section>;
}